import { Select, Space } from 'antd';
import { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { DateRangeValue } from 'technical/types';
import { DatePicker } from 'ui/form/date-picker';
import { DateRangePicker } from 'ui/form/date-range-picker';

import 'technical/time-utils/dayjs.timezone';

import { getPeriodBounds, getSingleDateBounds } from './date-bounds';
import styles from './index.module.scss';

export enum DateSelectionTypeEnum {
  Period = 'period',
  Date = 'date',
  Week = 'week',
  Month = 'month',
}

export interface TemporalitySelectInputProps {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  defaultType?: DateSelectionTypeEnum;
  allowClear?: boolean;
  allowEmpty?: boolean;
  // Must already be anchored on the construction site timezone (e.g.
  // `dayjs().tz()`) — used as-is to compute the default range for every mode.
  defaultDate?: Dayjs | null;
  excludedOptions?: DateSelectionTypeEnum[];
  noDefaultFor?: DateSelectionTypeEnum[];
}

const TemporalitySelectInput = ({
  value,
  onChange,
  defaultType = DateSelectionTypeEnum.Period,
  allowEmpty = true,
  allowClear = true,
  defaultDate = null,
  excludedOptions = [],
  noDefaultFor = [],
}: TemporalitySelectInputProps) => {
  const { t } = useTranslation();
  const [currentType, setCurrentType] =
    useState<DateSelectionTypeEnum>(defaultType);

  const range = value ?? [null, null];

  const [min] = range;

  const handleDataReset = () => {
    if (onChange) {
      onChange([null, null]);
    }
  };

  const handleDatesChange = (dates: DateRangeValue) => {
    if (dates && onChange) {
      onChange(dates);
    } else {
      handleDataReset(); // if we have no data the user click clear button
    }
  };

  const handleSelectChange = (type: DateSelectionTypeEnum) => {
    setCurrentType(type);
    if (defaultDate && !noDefaultFor.includes(type)) {
      // if we have a default date we assume we never want an empty date, so we determine a date according to the default date
      // another approach could use the previous date value in select
      switch (type) {
        case DateSelectionTypeEnum.Period:
          handleDatesChange(
            getPeriodBounds(defaultDate.subtract(1, 'week'), defaultDate),
          );
          break;
        case DateSelectionTypeEnum.Date:
        case DateSelectionTypeEnum.Week:
        case DateSelectionTypeEnum.Month:
          handleDatesChange(getSingleDateBounds(defaultDate, type));
          break;
      }
    } else {
      handleDataReset();
    }
  };

  const options = [
    {
      label: t('time.temporalitySelect.period'),
      value: DateSelectionTypeEnum.Period,
    },
    {
      label: t('time.temporalitySelect.date'),
      value: DateSelectionTypeEnum.Date,
    },
    {
      label: t('time.temporalitySelect.week'),
      value: DateSelectionTypeEnum.Week,
    },
    {
      label: t('time.temporalitySelect.month'),
      value: DateSelectionTypeEnum.Month,
    },
  ].filter((option) => !excludedOptions.includes(option.value));

  return (
    <Space direction={'horizontal'}>
      <Select
        value={currentType}
        onChange={handleSelectChange}
        options={options}
        className={styles.tempoSelector}
      />

      {currentType === DateSelectionTypeEnum.Period ? (
        <DateRangePicker
          value={range}
          className={styles.range}
          allowClear={allowClear}
          allowEmpty={allowEmpty}
          onChange={(dates) =>
            // dates come straight from the calendar cells the user clicked:
            // keep the picked day/month/year as-is and only reinterpret it
            // under the construction site timezone (`.tz(undefined, true)`,
            // keepLocalTime) rather than converting the instant, which would
            // shift the picked day depending on the browser/site UTC offset.
            handleDatesChange(
              getPeriodBounds(
                dates?.[0]?.tz(undefined, true),
                dates?.[1]?.tz(undefined, true),
              ),
            )
          }
          format="L"
        />
      ) : (
        <DatePicker
          value={min}
          allowClear={allowClear}
          className={styles.date}
          onChange={(date) => {
            if (date) {
              // same reasoning as the DateRangePicker onChange above: keep
              // the picked day, reinterpreted under the site timezone.
              handleDatesChange(
                getSingleDateBounds(date.tz(undefined, true), currentType),
              );
            } else {
              handleDataReset();
            }
          }}
          picker={currentType}
          format="L"
        />
      )}
    </Space>
  );
};

export default TemporalitySelectInput;
